import { useEffect, useRef, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  useEditMode,
  EditableToken,
  EditableSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

/**
 * Home-page Wine & Dine logo film + CTA from the live site
 * (https://www.woodincreekvillage.com/): tenant logos on white, then a
 * full-bleed gold bar with a WINE & DINE button and tagline.
 */

const SLIDE_INTERVAL_MS = 4500;

const ctaDef: SlotGroupDefinition = {
  label: 'Wine & Dine CTA',
  fields: {
    url: { label: 'Button URL', type: 'url', defaultValue: '/wine-dine' },
    label: { label: 'Button Label', type: 'text', defaultValue: 'WINE & DINE' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

const logo1: SlotGroupDefinition = {
  label: 'Logo 1 — Ma‘ono Fried Chicken',
  fields: {
    image: { label: 'Logo', type: 'image', defaultValue: 'https://images.squarespace-cdn.com/content/v1/678dcb3f2830c94f09790da2/3543604c-1824-4517-b88d-db8970c7ebd0/favicon.ico?format=500w' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Ma‘ono Fried Chicken' },
    url: { label: 'Link URL', type: 'url', defaultValue: '/wine-dine' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

const logo3: SlotGroupDefinition = {
  label: 'Logo 3 — Truth Teller',
  fields: {
    image: { label: 'Logo', type: 'image', defaultValue: 'https://irp.cdn-website.com/f79dba72/dms3rep/multi/Truth+teller.png' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Truth Teller Winery' },
    url: { label: 'Link URL', type: 'url', defaultValue: '/wine-dine' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

const logo4: SlotGroupDefinition = {
  label: 'Logo 4 — Sidekick Coffee',
  fields: {
    image: { label: 'Logo', type: 'image', defaultValue: 'https://irp.cdn-website.com/f79dba72/dms3rep/multi/sidekick-2a06955f.png' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Sidekick Coffee' },
    url: { label: 'Link URL', type: 'url', defaultValue: '/wine-dine' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

const logo5: SlotGroupDefinition = {
  label: 'Logo 5 — Rusty Pelican Café',
  fields: {
    image: { label: 'Logo', type: 'image', defaultValue: 'https://irp.cdn-website.com/f79dba72/dms3rep/multi/Untitled-1.png' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Rusty Pelican Café' },
    url: { label: 'Link URL', type: 'url', defaultValue: '/wine-dine' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

const logo7: SlotGroupDefinition = {
  label: 'Logo 7 — Ambassador Wines',
  fields: {
    image: { label: 'Logo', type: 'image', defaultValue: 'https://irp.cdn-website.com/f79dba72/dms3rep/multi/amb2.png' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Ambassador Wines' },
    url: { label: 'Link URL', type: 'url', defaultValue: '/wine-dine' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

const logo8: SlotGroupDefinition = {
  label: 'Logo 8 — Fresh Cuts',
  fields: {
    image: { label: 'Logo', type: 'image', defaultValue: 'https://irp.cdn-website.com/f79dba72/dms3rep/multi/fresh+cuts-8f6c5e73.png' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Fresh Cuts' },
    url: { label: 'Link URL', type: 'url', defaultValue: '/wine-dine' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

const logo9: SlotGroupDefinition = {
  label: 'Logo 9 — Brix Wine Café',
  fields: {
    image: { label: 'Logo', type: 'image', defaultValue: 'https://irp.cdn-website.com/f79dba72/dms3rep/multi/brix.png' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Brix Wine Café' },
    url: { label: 'Link URL', type: 'url', defaultValue: '/wine-dine' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

const logo10: SlotGroupDefinition = {
  label: 'Logo 10 — Baer Winery',
  fields: {
    image: { label: 'Logo', type: 'image', defaultValue: 'https://irp.cdn-website.com/f79dba72/dms3rep/multi/imgpsh_fullsize_anim+%281%29.png' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Baer Winery' },
    url: { label: 'Link URL', type: 'url', defaultValue: '/wine-dine' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

const logo11: SlotGroupDefinition = {
  label: 'Logo 11 — Swanky Scoop',
  fields: {
    image: { label: 'Logo', type: 'image', defaultValue: 'https://irp.cdn-website.com/f79dba72/dms3rep/multi/circle-cropped.png' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Swanky Scoop' },
    url: { label: 'Link URL', type: 'url', defaultValue: '/wine-dine' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

const logo12: SlotGroupDefinition = {
  label: 'Logo 12 — Woodin Creek Kitchen and Tap',
  fields: {
    image: { label: 'Logo', type: 'image', defaultValue: 'https://irp.cdn-website.com/f79dba72/dms3rep/multi/large-image+%281%29.png' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Woodin Creek Kitchen and Tap' },
    url: { label: 'Link URL', type: 'url', defaultValue: '/wine-dine' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

const logo13: SlotGroupDefinition = {
  label: 'Logo 13 — Bayernmoor Cellars',
  fields: {
    image: { label: 'Logo', type: 'image', defaultValue: 'https://irp.cdn-website.com/f79dba72/dms3rep/multi/Bayernmoor.png' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Bayernmoor Cellars' },
    url: { label: 'Link URL', type: 'url', defaultValue: '/wine-dine' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

const card1: SlotGroupDefinition = {
  label: 'Card 1 — Floor Plans',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[0].alt_text' },
    label: { label: 'CTA Label', type: 'text', defaultValue: 'View Floor Plans' },
    url: { label: 'CTA URL', type: 'url', defaultValue: '/floor-plans' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt: 'A real apartment interior photo at this property that suits a link to the floor plans page.',
    context: 'Card thumb 1 of 5 in a link-card row on the home page (Floor Plans). Different photo from cards 2–5.',
  },
};

const card2: SlotGroupDefinition = {
  label: 'Card 2 — Amenities',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[1].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[1].alt_text' },
    label: { label: 'CTA Label', type: 'text', defaultValue: 'View Amenities' },
    url: { label: 'CTA URL', type: 'url', defaultValue: '/amenities' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 1,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt: 'A real photo of a community amenity at this property that suits a link to the amenities page.',
    context: 'Card thumb 2 of 5 in a link-card row on the home page (Amenities). Different photo from cards 1 and 3–5.',
  },
};

const card3: SlotGroupDefinition = {
  label: 'Card 3 — Gallery',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[2].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[2].alt_text' },
    label: { label: 'CTA Label', type: 'text', defaultValue: 'View Gallery' },
    url: { label: 'CTA URL', type: 'url', defaultValue: '/gallery' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 2,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt: 'A strong, representative photo of this property that suits a link to the photo gallery page.',
    context: 'Card thumb 3 of 5 in a link-card row on the home page (Gallery). Different photo from cards 1–2 and 4–5.',
  },
};

const card4: SlotGroupDefinition = {
  label: 'Card 4 — Neighborhood',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[3].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[3].alt_text' },
    label: { label: 'CTA Label', type: 'text', defaultValue: 'View Neighborhood' },
    url: { label: 'CTA URL', type: 'url', defaultValue: '/neighborhood' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 3,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt: 'A real exterior or surrounding-area photo of this property that suits a link to the neighborhood page.',
    context: 'Card thumb 4 of 5 in a link-card row on the home page (Neighborhood). Different photo from cards 1–3 and 5.',
  },
};

const card5: SlotGroupDefinition = {
  label: 'Card 5 — Contact Us',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[4].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[4].alt_text' },
    label: { label: 'CTA Label', type: 'text', defaultValue: 'Contact Us' },
    url: { label: 'CTA URL', type: 'url', defaultValue: '/contact-us' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 4,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt: 'A welcoming real photo of this property (leasing office, entry, or building exterior) that suits a link to the contact page.',
    context: 'Card thumb 5 of 5 in a link-card row on the home page (Contact Us). Different photo from cards 1–4.',
  },
};

const logos: { path: string; definition: SlotGroupDefinition }[] = [
  { path: 'winedinelogoslide.logo1', definition: logo1 },
  { path: 'winedinelogoslide.logo3', definition: logo3 },
  { path: 'winedinelogoslide.logo4', definition: logo4 },
  { path: 'winedinelogoslide.logo5', definition: logo5 },
  { path: 'winedinelogoslide.logo7', definition: logo7 },
  { path: 'winedinelogoslide.logo8', definition: logo8 },
  { path: 'winedinelogoslide.logo9', definition: logo9 },
  { path: 'winedinelogoslide.logo10', definition: logo10 },
  { path: 'winedinelogoslide.logo11', definition: logo11 },
  { path: 'winedinelogoslide.logo12', definition: logo12 },
  { path: 'winedinelogoslide.logo13', definition: logo13 },
];

function perViewForWidth(width: number): number {
  if (width >= 1200) return 6;
  if (width >= 992) return 5;
  if (width >= 768) return 4;
  return 2;
}

function LogoSlide({
  path,
  definition,
}: {
  path: string;
  definition: SlotGroupDefinition;
}) {
  const logo = useSlotGroup(path, definition);
  const editMode = useEditMode();
  const isEditing = !!editMode?.isEditMode;
  if (!logo.image && !isEditing) return null;

  const href = logo.url || '/wine-dine';
  const target = logo._target || '_self';
  const rel = target === '_blank' ? 'noopener noreferrer' : undefined;

  return (
    <EditableSlotGroup groupPath={path} definition={definition} className="wine-dine-logo-slide__item">
      <a href={href} target={target} rel={rel} className="wine-dine-logo-slide__link" aria-label={logo.alt || logo.image || 'Wine & Dine'}>
        {logo.image ? (
          <VibesitesImage
            url={logo.image}
            alt={logo.alt || ''}
            sizes="120px"
            className="wine-dine-logo-slide__img"
          />
        ) : (
          <span className="wine-dine-logo-slide__placeholder">{logo.alt || 'Logo'}</span>
        )}
      </a>
    </EditableSlotGroup>
  );
}

function PageCardLink({
  path,
  definition,
  fallbackLabel,
  fallbackUrl,
}: {
  path: string;
  definition: SlotGroupDefinition;
  fallbackLabel: string;
  fallbackUrl: string;
}) {
  const card = useSlotGroup(path, definition);
  const href = card.url || fallbackUrl;
  const target = card._target || '_self';
  const rel = target === '_blank' ? 'noopener noreferrer' : undefined;

  return (
    <a href={href} target={target} rel={rel} className="wine-dine-cards__link">
      <span className="wine-dine-cards__slide">
        {card.image ? (
          <VibesitesImage
            url={card.image}
            alt={card.alt || ''}
            sizes="(min-width: 992px) 20vw, (min-width: 576px) 50vw, 100vw"
            className="wine-dine-cards__img"
          />
        ) : (
          <span className="wine-dine-cards__placeholder bg-muted" aria-hidden="true" />
        )}
        <span className="wine-dine-cards__cta font-heading">{card.label || fallbackLabel}</span>
      </span>
    </a>
  );
}

export function WineDineLogoSlide() {
  const tagline = useContentToken('winedinelogoslide.tagline');
  const cta = useSlotGroup('winedinelogoslide.cta', ctaDef);
  const editMode = useEditMode();
  const isEditing = !!editMode?.isEditMode;

  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const [perView, setPerView] = useState(5);
  const [reducedMotion, setReducedMotion] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const cardsRef = useRef<HTMLDivElement | null>(null);
  const [cardsReveal, setCardsReveal] = useState<'static' | 'armed' | 'revealed'>('static');

  const logo1Data = useSlotGroup('winedinelogoslide.logo1', logo1);
  const logo3Data = useSlotGroup('winedinelogoslide.logo3', logo3);
  const logo4Data = useSlotGroup('winedinelogoslide.logo4', logo4);
  const logo5Data = useSlotGroup('winedinelogoslide.logo5', logo5);
  const logo7Data = useSlotGroup('winedinelogoslide.logo7', logo7);
  const logo8Data = useSlotGroup('winedinelogoslide.logo8', logo8);
  const logo9Data = useSlotGroup('winedinelogoslide.logo9', logo9);
  const logo10Data = useSlotGroup('winedinelogoslide.logo10', logo10);
  const logo11Data = useSlotGroup('winedinelogoslide.logo11', logo11);
  const logo12Data = useSlotGroup('winedinelogoslide.logo12', logo12);
  const logo13Data = useSlotGroup('winedinelogoslide.logo13', logo13);
  const logoData = [
    logo1Data,
    logo3Data,
    logo4Data,
    logo5Data,
    logo7Data,
    logo8Data,
    logo9Data,
    logo10Data,
    logo11Data,
    logo12Data,
    logo13Data,
  ];

  const visibleCount = isEditing
    ? logos.length
    : logoData.filter((item) => item.image).length;
  const maxIndex = Math.max(0, visibleCount - perView);

  useEffect(() => {
    const applyWidth = () => setPerView(perViewForWidth(window.innerWidth));
    applyWidth();
    window.addEventListener('resize', applyWidth);
    return () => window.removeEventListener('resize', applyWidth);
  }, []);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => setReducedMotion(mq.matches);
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  useEffect(() => {
    const el = cardsRef.current;
    if (!el || isEditing || reducedMotion || typeof IntersectionObserver === 'undefined') {
      setCardsReveal('static');
      return;
    }
    setCardsReveal((prev) => (prev === 'revealed' ? prev : 'armed'));
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setCardsReveal('revealed');
          observer.disconnect();
        }
      },
      { threshold: 0.15 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [isEditing, reducedMotion]);

  useEffect(() => {
    if (current > maxIndex) setCurrent(0);
  }, [current, maxIndex]);

  useEffect(() => {
    if (visibleCount <= perView || paused || reducedMotion || isEditing) return;
    timerRef.current = setInterval(() => {
      setCurrent((prev) => (prev >= maxIndex ? 0 : prev + 1));
    }, SLIDE_INTERVAL_MS);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [visibleCount, perView, paused, reducedMotion, isEditing, maxIndex]);

  const goPrev = () => setCurrent((prev) => (prev <= 0 ? maxIndex : prev - 1));
  const goNext = () => setCurrent((prev) => (prev >= maxIndex ? 0 : prev + 1));

  const ctaHref = cta.url || '/wine-dine';
  const ctaTarget = cta._target || '_self';
  const ctaRel = ctaTarget === '_blank' ? 'noopener noreferrer' : undefined;

  return (
    <section className="wine-dine-logo-slide bg-background">
      <div
        className="wine-dine-logo-slide__carousel"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        aria-roledescription="carousel"
        aria-label="Village restaurants and wineries"
      >
        {visibleCount > perView ? (
          <button
            type="button"
            className="wine-dine-logo-slide__arrow wine-dine-logo-slide__arrow--prev"
            onClick={goPrev}
            aria-label="Previous logos"
          >
            ‹
          </button>
        ) : null}

        <div className="wine-dine-logo-slide__viewport">
          <div
            className="wine-dine-logo-slide__track"
            style={{
              transform: `translateX(calc(-${current} * (100% / ${perView})))`,
              ['--wine-dine-per-view' as string]: String(perView),
            }}
          >
            {logos.map((logo) => (
              <LogoSlide key={logo.path} path={logo.path} definition={logo.definition} />
            ))}
          </div>
        </div>

        {visibleCount > perView ? (
          <button
            type="button"
            className="wine-dine-logo-slide__arrow wine-dine-logo-slide__arrow--next"
            onClick={goNext}
            aria-label="Next logos"
          >
            ›
          </button>
        ) : null}
      </div>

      <div className="wine-dine-logo-slide__cta-bar bg-secondary">
        <div className="wine-dine-logo-slide__cta-row">
          <EditableSlotGroup
            groupPath="winedinelogoslide.cta"
            definition={ctaDef}
            className="wine-dine-logo-slide__cta-wrap"
          >
            <a
              href={ctaHref}
              target={ctaTarget}
              rel={ctaRel}
              className="wine-dine-logo-slide__cta text-secondary-foreground"
            >
              {cta.label || 'WINE & DINE'}
            </a>
          </EditableSlotGroup>
          <EditableToken
            path="winedinelogoslide.tagline"
            as="h2"
            className="wine-dine-logo-slide__tagline font-heading text-secondary-foreground"
          >
            {tagline || 'Award winning restaurants and wineries'}
          </EditableToken>
        </div>
      </div>

      <div ref={cardsRef} className={`wine-dine-cards wine-dine-cards--${cardsReveal}`}>
        <EditableSlotGroup groupPath="winedinelogoslide.card1" definition={card1} className="wine-dine-cards__item" style={{ ['--wine-dine-card-delay' as string]: '0ms' }}>
          <PageCardLink path="winedinelogoslide.card1" definition={card1} fallbackLabel="View Floor Plans" fallbackUrl="/floor-plans" />
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="winedinelogoslide.card2" definition={card2} className="wine-dine-cards__item" style={{ ['--wine-dine-card-delay' as string]: '120ms' }}>
          <PageCardLink path="winedinelogoslide.card2" definition={card2} fallbackLabel="View Amenities" fallbackUrl="/amenities" />
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="winedinelogoslide.card3" definition={card3} className="wine-dine-cards__item" style={{ ['--wine-dine-card-delay' as string]: '240ms' }}>
          <PageCardLink path="winedinelogoslide.card3" definition={card3} fallbackLabel="View Gallery" fallbackUrl="/gallery" />
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="winedinelogoslide.card4" definition={card4} className="wine-dine-cards__item" style={{ ['--wine-dine-card-delay' as string]: '360ms' }}>
          <PageCardLink path="winedinelogoslide.card4" definition={card4} fallbackLabel="View Neighborhood" fallbackUrl="/neighborhood" />
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="winedinelogoslide.card5" definition={card5} className="wine-dine-cards__item" style={{ ['--wine-dine-card-delay' as string]: '480ms' }}>
          <PageCardLink path="winedinelogoslide.card5" definition={card5} fallbackLabel="Contact Us" fallbackUrl="/contact-us" />
        </EditableSlotGroup>
      </div>

      <EditableSlotGroup groupPath="winedinelogoslide.logo1" definition={logo1} className="hidden" style={{ display: 'none' }}><span /></EditableSlotGroup>
      <EditableSlotGroup groupPath="winedinelogoslide.logo3" definition={logo3} className="hidden" style={{ display: 'none' }}><span /></EditableSlotGroup>
      <EditableSlotGroup groupPath="winedinelogoslide.logo4" definition={logo4} className="hidden" style={{ display: 'none' }}><span /></EditableSlotGroup>
      <EditableSlotGroup groupPath="winedinelogoslide.logo5" definition={logo5} className="hidden" style={{ display: 'none' }}><span /></EditableSlotGroup>
      <EditableSlotGroup groupPath="winedinelogoslide.logo7" definition={logo7} className="hidden" style={{ display: 'none' }}><span /></EditableSlotGroup>
      <EditableSlotGroup groupPath="winedinelogoslide.logo8" definition={logo8} className="hidden" style={{ display: 'none' }}><span /></EditableSlotGroup>
      <EditableSlotGroup groupPath="winedinelogoslide.logo9" definition={logo9} className="hidden" style={{ display: 'none' }}><span /></EditableSlotGroup>
      <EditableSlotGroup groupPath="winedinelogoslide.logo10" definition={logo10} className="hidden" style={{ display: 'none' }}><span /></EditableSlotGroup>
      <EditableSlotGroup groupPath="winedinelogoslide.logo11" definition={logo11} className="hidden" style={{ display: 'none' }}><span /></EditableSlotGroup>
      <EditableSlotGroup groupPath="winedinelogoslide.logo12" definition={logo12} className="hidden" style={{ display: 'none' }}><span /></EditableSlotGroup>
      <EditableSlotGroup groupPath="winedinelogoslide.logo13" definition={logo13} className="hidden" style={{ display: 'none' }}><span /></EditableSlotGroup>
      <EditableSlotGroup groupPath="winedinelogoslide.cta" definition={ctaDef} className="hidden" style={{ display: 'none' }}><span /></EditableSlotGroup>
      <EditableToken path="winedinelogoslide.tagline" as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>

      <style>{`
        .wine-dine-logo-slide {
          padding: 0;
        }

        .wine-dine-logo-slide__carousel {
          position: relative;
          max-width: 1100px;
          margin: 0 auto;
          display: flex;
          align-items: center;
          gap: 0.35rem;
          padding: 1.25rem 1rem 1rem;
        }
        @media (min-width: 768px) {
          .wine-dine-logo-slide__carousel { padding: 1.5rem 1.5rem 1.15rem; }
        }
        @media (min-width: 1200px) {
          .wine-dine-logo-slide__carousel { padding: 1.75rem 2rem 1.25rem; }
        }

        .wine-dine-logo-slide__viewport {
          overflow: hidden;
          flex: 1;
          min-width: 0;
        }

        .wine-dine-logo-slide__track {
          display: flex;
          width: 100%;
          transition: transform 0.55s cubic-bezier(0.22, 1, 0.36, 1);
        }
        @media (prefers-reduced-motion: reduce) {
          .wine-dine-logo-slide__track { transition: none; }
        }

        .wine-dine-logo-slide__item {
          flex: 0 0 calc(100% / var(--wine-dine-per-view, 1));
          display: flex;
          justify-content: center;
          padding: 0.2rem 0.4rem;
          box-sizing: border-box;
        }

        .wine-dine-logo-slide__link {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 88px;
          height: 88px;
          max-width: 100%;
          text-decoration: none;
        }
        @media (min-width: 768px) {
          .wine-dine-logo-slide__link {
            width: 104px;
            height: 104px;
          }
        }
        @media (min-width: 1200px) {
          .wine-dine-logo-slide__link {
            width: 112px;
            height: 112px;
          }
        }

        .wine-dine-logo-slide__img {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }

        .wine-dine-logo-slide__placeholder {
          padding: 1rem;
          text-align: center;
          font-family: var(--vibe-font-heading);
          font-size: 0.85rem;
          text-transform: uppercase;
          color: var(--vibe-color-text-muted);
        }

        .wine-dine-logo-slide__arrow {
          flex-shrink: 0;
          width: 1.75rem;
          height: 1.75rem;
          border: 1px solid var(--vibe-color-secondary);
          border-radius: 9999px;
          background: transparent;
          color: var(--vibe-color-secondary);
          font-size: 1.25rem;
          line-height: 1;
          cursor: pointer;
        }
        .wine-dine-logo-slide__arrow:hover {
          background: var(--vibe-color-secondary);
          color: var(--vibe-color-text-on-secondary);
        }
        @media (max-width: 767px) {
          .wine-dine-logo-slide__arrow {
            position: absolute;
            z-index: 2;
            top: 50%;
            transform: translateY(-50%);
            background: var(--vibe-color-background);
          }
          .wine-dine-logo-slide__arrow--prev { left: 0.35rem; }
          .wine-dine-logo-slide__arrow--next { right: 0.35rem; }
        }

        .wine-dine-logo-slide__cta-bar {
          width: 100%;
        }

        .wine-dine-logo-slide__cta-row {
          max-width: 1100px;
          margin: 0 auto;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 0.55rem;
          padding: 0.7rem 1rem;
          text-align: center;
        }
        @media (min-width: 768px) {
          .wine-dine-logo-slide__cta-row {
            flex-direction: row;
            gap: 1rem 1.5rem;
            padding: 0.75rem 1.5rem;
            text-align: left;
          }
        }

        .wine-dine-logo-slide__cta-wrap {
          display: block;
          width: fit-content;
          flex-shrink: 0;
        }

        .wine-dine-logo-slide__cta {
          display: flex;
          align-items: center;
          justify-content: center;
          box-sizing: border-box;
          min-width: 10rem;
          height: 36px;
          padding: 0 1rem;
          border: 1px solid currentColor;
          background: transparent;
          font-family: var(--vibe-font-body);
          font-size: 13px;
          font-weight: 700;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          text-decoration: none;
          border-radius: 0;
          transition: background 0.25s ease, color 0.25s ease;
        }
        .wine-dine-logo-slide__cta:hover {
          background: var(--vibe-color-text-on-secondary);
          color: var(--vibe-color-secondary);
        }

        .wine-dine-logo-slide__tagline {
          margin: 0;
          font-size: 0.95rem;
          font-weight: 400;
          line-height: 1.3;
        }
        @media (min-width: 768px) {
          .wine-dine-logo-slide__tagline { font-size: 1.1rem; }
        }

        .wine-dine-cards {
          display: grid;
          grid-template-columns: 1fr;
          gap: 4px;
          padding: 6px;
        }
        @media (min-width: 576px) {
          .wine-dine-cards { grid-template-columns: repeat(2, 1fr); }
          .wine-dine-cards > :last-child { grid-column: 1 / -1; }
        }
        @media (min-width: 992px) {
          .wine-dine-cards { grid-template-columns: repeat(5, 1fr); }
          .wine-dine-cards > :last-child { grid-column: auto; }
        }

        .wine-dine-cards__item {
          display: block;
          min-width: 0;
        }

        .wine-dine-cards__link {
          position: relative;
          display: block;
          aspect-ratio: 4 / 3;
          overflow: hidden;
          text-decoration: none;
        }

        .wine-dine-cards__slide {
          position: absolute;
          inset: 0;
          display: block;
        }
        .wine-dine-cards__slide::after {
          content: '';
          position: absolute;
          inset: 0;
          background: rgba(0, 0, 0, 0.35);
          pointer-events: none;
          transition: background 0.3s ease;
        }
        .wine-dine-cards__link:hover .wine-dine-cards__slide::after,
        .wine-dine-cards__link:focus-visible .wine-dine-cards__slide::after {
          background: rgba(0, 0, 0, 0.2);
        }

        @keyframes wine-dine-card-slide-in-down {
          from { transform: translate3d(0, -100%, 0); }
          to { transform: translate3d(0, 0, 0); }
        }
        .wine-dine-cards--armed .wine-dine-cards__slide {
          transform: translate3d(0, -100%, 0);
        }
        .wine-dine-cards--revealed .wine-dine-cards__slide {
          animation: wine-dine-card-slide-in-down 0.9s cubic-bezier(0.22, 1, 0.36, 1) var(--wine-dine-card-delay, 0ms) both;
        }
        @media (prefers-reduced-motion: reduce) {
          .wine-dine-cards--armed .wine-dine-cards__slide { transform: none; }
          .wine-dine-cards--revealed .wine-dine-cards__slide { animation: none; }
        }

        .wine-dine-cards__img,
        .wine-dine-cards__placeholder {
          display: block;
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.5s ease;
        }
        .wine-dine-cards__link:hover .wine-dine-cards__img { transform: scale(1.04); }
        @media (prefers-reduced-motion: reduce) {
          .wine-dine-cards__img { transition: none; }
          .wine-dine-cards__link:hover .wine-dine-cards__img { transform: none; }
        }

        .wine-dine-cards__cta {
          position: absolute;
          z-index: 1;
          left: 0.65rem;
          bottom: 0.75rem;
          right: 0.65rem;
          color: #fff;
          font-size: 0.75rem;
          font-weight: 500;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          line-height: 1.2;
        }
        .wine-dine-cards__cta::after {
          content: ' \\2192';
        }
        .wine-dine-cards__link:hover .wine-dine-cards__cta,
        .wine-dine-cards__link:focus-visible .wine-dine-cards__cta {
          text-decoration: underline;
          text-underline-offset: 4px;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'WineDineLogoSlide',
  displayName: 'Wine & Dine Logo Slide',
  tokens: {
    'winedinelogoslide.tagline': {
      label: 'Tagline',
      type: 'text',
      defaultValue: 'Award winning restaurants and wineries',
    },
  },
  slotGroups: {
    'winedinelogoslide.cta': ctaDef,
    'winedinelogoslide.logo1': logo1,
    'winedinelogoslide.logo3': logo3,
    'winedinelogoslide.logo4': logo4,
    'winedinelogoslide.logo5': logo5,
    'winedinelogoslide.logo7': logo7,
    'winedinelogoslide.logo8': logo8,
    'winedinelogoslide.logo9': logo9,
    'winedinelogoslide.logo10': logo10,
    'winedinelogoslide.logo11': logo11,
    'winedinelogoslide.logo12': logo12,
    'winedinelogoslide.logo13': logo13,
    'winedinelogoslide.card1': card1,
    'winedinelogoslide.card2': card2,
    'winedinelogoslide.card3': card3,
    'winedinelogoslide.card4': card4,
    'winedinelogoslide.card5': card5,
  },
};

(WineDineLogoSlide as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default WineDineLogoSlide;
